import injectSheet, { createStyles } from 'core/style/injectSheet';
import { ITheme, WithStyles } from 'core/style/interfaces';
import { formatToLocalDayMonthYear } from 'core/utils';
import React from 'react';
import { GetDividendDetailQuery } from 'site/graphql';
import CheckIcon from '../../../../core/svg/check-simple.svg';
import ClockIcon from '../../../svg/clock.svg';
import CycleIcon from '../../../svg/cycle.svg';
import { numberFormatByValue } from '../const';
import DividendTableHeadCell from '../DividendTableHeadCell';

type IDividendDetailHistoryProps = {
    history: GetDividendDetailQuery['dividend']['history'];
} & WithStyles<typeof styles>;

const DividendDetailHistory: React.FC<IDividendDetailHistoryProps> = ({ history, classes }) => (
    <div className={classes.tableWrapper}>
        <table className={classes.table}>
            <thead>
                <tr>
                    <DividendTableHeadCell
                        tooltip="3М (3 месяца), 6М (6 месяцев), 9М (9 месяцев), год, Прибыль прошедших лет (нераспределенная чистая прибыль)"
                        title="За какой период работы был выплачен дивиденд"
                    />
                    <DividendTableHeadCell
                        tooltip="До конца какого дня нужно купить акцию, чтобы попасть в реестр и получить дивиденды"
                        title="Последний день, чтобы купить"
                    />
                    <DividendTableHeadCell
                        tooltip="Когда эмитент выплатит дивиденд инвестору"
                        title="Дата закрытия реестра"
                    />
                    <DividendTableHeadCell tooltip="Размер дивиденда" title="Дивиденд (₽)" />
                    <DividendTableHeadCell
                        tooltip={
                            <>
                                <CheckIcon className={classes.iconTooltip} />
                                {' — доходность по цене закрытия на отсечку,'}
                                <br />
                                <CycleIcon className={classes.iconTooltip} />
                                {' — текущая доходность ближ. дивиденда'}
                            </>
                        }
                        title="Доходность дивиденда по тек. цене или цене отсечки (%)"
                        colSpan={2}
                    />
                    <DividendTableHeadCell
                        tooltip="На сколько упала акция после дивидендной отсечки"
                        title="Размер див. гэпа (%)"
                    />
                    <DividendTableHeadCell
                        tooltip={
                            <>
                                {'Сколько календарных дней закрывался гэп после этого дивиденда. '}
                                <br />
                                <ClockIcon className={classes.iconTooltip} />
                                {' — если еще не закрылся'}
                            </>
                        }
                        title="Срок закрытия див. гэпа (день)"
                    />
                </tr>
            </thead>
            <tbody>
                {history.map((h, i) => (
                    <tr key={i}>
                        <td>{h.period}</td>
                        <td>{formatToLocalDayMonthYear(h.lastDayToBuy)}</td>
                        <td>{formatToLocalDayMonthYear(h.registryClose)}</td>
                        <td>{numberFormatByValue(h.dividend)}</td>
                        <td style={{ width: '1%' }}>
                            {h.paymentYield.toFixed(2)} / {numberFormatByValue(h.yield)}
                        </td>
                        <td style={{ minWidth: 40 }}>
                            {h.paid ? <CheckIcon className={classes.icon} /> : <CycleIcon className={classes.icon} />}
                        </td>
                        <td>
                            {h.gapDawn !== null ? h.gapDawn : ' '}
                        </td>
                        <td>
                            <div className={classes.iconCell}>
                                {h.timeGap || ''}
                                {!h.gapClosed && <ClockIcon className={classes.icon} />}
                            </div>
                        </td>
                    </tr>
                ))}
            </tbody>
        </table>
    </div>
);

const styles = (theme: ITheme) =>
    createStyles({
        tableWrapper: {
            [theme.breakpoints.down('md')]: {
                overflowX: 'auto',
            },
        },
        table: {
            borderCollapse: 'collapse',
            position: 'relative',

            '& tr': {
                borderBottom: '1px solid #E5E5E5',
            },

            '& tr:hover': {
                backgroundColor: 'rgba(0, 0, 0, 0.05)',
            },

            '& td, th': {
                padding: 5,
            },

            '& th': {
                minWidth: 150,
            },

            '& td': {
                fontSize: 14,
            },
        },
        icon: {
            width: 14,
            height: 14,
            fill: '#D9D9D9',
        },
        iconTooltip: {
            width: 14,
            height: 14,
            fill: '#808080',
            verticalAlign: 'middle',
        },
        iconCell: {
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
        },
    });

export default injectSheet(styles)(DividendDetailHistory);
